import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/data';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-600 text-sm mb-4">请检查链接是否正确</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    url: `https://teadaye.cn/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '水蜜桃视频',
    },
    articleBody: article.content.replace(/<[^>]*>/g, '').slice(0, 500),
    keywords: article.tags.join(','),
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 水蜜桃视频`}
        description={article.excerpt}
        keywords={`水蜜桃视频,${article.tags.join(',')}`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
              {article.categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span>{article.author}</span>
              </div>
              <span>{article.publishDate}</span>
              <span>{article.readTime}</span>
            </div>
          </div>

          <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverUrl}
              alt={article.title}
              title={`${article.title} - 水蜜桃视频`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} {...ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}